import { 
  LayoutDashboard, 
  MapPin,
  CalendarDays,
  Store,
  Package,
  Truck,
  MoreHorizontal,
  Clock,
  ClipboardList,
  Warehouse,
  Bed,
  BookOpen,
} from 'lucide-react';
import type { NavConfig } from './nav.types';

export const ACTIVITY_OFFERING_KEYS = ['events', 'courses_workshops', 'guided_tours'] as const;
export const SHOP_OFFERING_KEYS = ['farm_shop', 'preorder_pickup', 'other_farm_sales', 'handelstradgard'] as const;
export const FLEA_MARKET_OFFERING_KEYS = ['flea_market'] as const;
export const ACCOMMODATION_OFFERING_KEYS = ['accommodation'] as const;

export interface ProducerNavVisibility {
  showActivities: boolean;
  showShop: boolean;
  showFleaMarket: boolean;
  showAccommodation: boolean;
}

export function getProducerNavVisibility(offeringKeys: string[]): ProducerNavVisibility {
  const keySet = new Set(offeringKeys);
  return {
    showActivities: ACTIVITY_OFFERING_KEYS.some((key) => keySet.has(key)),
    showShop: SHOP_OFFERING_KEYS.some((key) => keySet.has(key)),
    showFleaMarket: FLEA_MARKET_OFFERING_KEYS.some((key) => keySet.has(key)),
    showAccommodation: ACCOMMODATION_OFFERING_KEYS.some((key) => keySet.has(key)),
  };
}

/**
 * Producer nav — desktop sidebar & mobile bottom nav.
 * Matches the mobile bottom nav structure:
 * Översikt | Aktiviteter | Gårdsbutik | Plats | Mer
 *
 * Children are rendered as collapsible sub-menus on desktop.
 * Mobile bottom nav ignores children (flat 5-item bar).
 */
export function getProducerNav(farmId: string): NavConfig {
  return [
    {
      title: 'Gårdshantering',
      items: [
        {
          label: 'Översikt',
          href: `/app/producer/farm/${farmId}`,
          icon: LayoutDashboard,
          exact: true,
        },
        {
          label: 'Aktiviteter',
          href: `/app/producer/farm/${farmId}/events`,
          icon: CalendarDays,
        },
        {
          label: 'Boende',
          href: `/app/producer/farm/${farmId}/accommodation`,
          icon: Bed,
        },
        {
          label: 'Loppis',
          href: `/app/producer/farm/${farmId}/loppis`,
          icon: Warehouse,
        },
        {
          label: 'Gårdsbutik',
          href: `/app/producer/farm/${farmId}/shop`,
          icon: Store,
          children: [
            {
              label: 'Produkter',
              href: `/app/producer/farm/${farmId}/shop/products`,
              icon: Package,
            },
            {
              label: 'Beställningar',
              href: `/app/producer/farm/${farmId}/shop/orders`,
              icon: Store,
              badgeKey: 'newOrders',
            },
            {
              label: 'Upphämtning',
              href: `/app/producer/farm/${farmId}/shop/pickup-windows`,
              icon: Clock,
            },
            {
              label: 'Packlista',
              href: `/app/producer/farm/${farmId}/shop/pack-list`,
              icon: ClipboardList,
            },
            {
              label: 'Frakt & leverans',
              href: `/app/producer/farm/${farmId}/shop/shipping`,
              icon: Truck,
            },
          ],
        },
        {
          label: 'Bokningar',
          href: `/app/producer/farm/${farmId}/bookings`,
          icon: BookOpen,
          activeMatchPrefixes: [
            `/app/producer/farm/${farmId}/bookings`,
            `/app/producer/farm/${farmId}/events/`,
          ],
        },
        {
          label: 'Sida',
          href: `/app/producer/farm/${farmId}/plats`,
          icon: MapPin,
          viewerReadOnly: true,
        },
        {
          label: 'Mer',
          href: `/app/producer/farm/${farmId}/mer`,
          icon: MoreHorizontal,
        },
      ],
    },
  ];
}
